// global imports
import { CircularProgress, makeStyles, Theme } from '@material-ui/core';
import cx from 'classnames';
import React, { useEffect, useState } from 'react';
import { withLocalize } from 'react-localize-redux';
import { connect } from 'react-redux';

// project imports
import Button from '../../../../components/CustomButtons/Button.jsx';
import * as authActions from '../../../../store/actions/auth_actions';
import * as shopifyActions from '../../../../store/actions/shopify_actions';

// project types imports
import { IState } from '../../../../store/reducers';
import { IStyleProps, PropsClasses } from '../../../../types/StyleProps';

// local imports
import commonStyles from '../../../../assets/jss/commonStyles';
import { DispatchProps, OwnProps, Props, StateProps } from './types';

const useStyles = makeStyles<Theme, IStyleProps>(() => commonStyles);

const ImportProducts: React.FC<Props & typeof defaultProps> = props => {
  const classes: PropsClasses = useStyles({} as IStyleProps);

  const {
    translate,
    OAuthURL,
    testConnection,
    shopifyLastRun,
    shopifyImportProductsStatus,
    testConnectionTimeout,
    connectionStatus,
    showSpinner,
    fetchOAuthUrlRequest,
    shopifyShops,
    testConnectionForShopRequest,
  } = props;

  const [testDelayActive, setTestDelayActive] = useState<any>(false);

  // didMount fetch data
  useEffect(() => {
    if (testConnection) {
      if (testConnectionTimeout) {
        setTestDelayActive(true);
      }
      setTimeout(() => {
        const shop = shopifyShops.find(s => s.active);
        if (shop) {
          testConnectionForShopRequest({ shop: shop.name });
        }
        if (!OAuthURL) {
          fetchOAuthUrlRequest({ system: 'shopify' });
        }
        setTestDelayActive(false);
      }, testConnectionTimeout);
    }
  }, [shopifyShops]);

  // test connection status
  useEffect(() => {
    if (connectionStatus) {
      if (connectionStatus === 'nok' || connectionStatus === 'nook') {
        fetchOAuthUrlRequest({ system: 'shopify' });
      }
    }
  }, [connectionStatus]);

  const importProducts = () => {
    const { importProductsRequest } = props;
    const shop = shopifyShops.find(s => s.active);
    if (shop) {
      importProductsRequest({ shop: shop.name });
    }
  };

  return (
    <>
      {testDelayActive || connectionStatus !== 'loading'
        ? connectionStatus === 'ok' && (
            <>
              <p className={classes.bodyLg}>{translate('import_all_products')}</p>
              <div
                className={cx(classes.runLabel, {
                  [classes.flexCenter]: !shopifyLastRun || shopifyLastRun.exitState !== 'error',
                })}
              >
                <Button className={classes.formButton} type="button" color="primary" onClick={importProducts}>
                  {translate('import_all_products')}
                </Button>
              </div>
              {shopifyImportProductsStatus === 'loading' && <CircularProgress />}
              {shopifyImportProductsStatus === 'ok' && (
                <p className={classes.iconGreen}>
                  {translate('successfully_imported', undefined, { renderInnerHtml: true })}
                </p>
              )}
              {shopifyImportProductsStatus === 'failed' && (
                <p className={classes.iconRed}>{translate('unsuccessfully_imported')}</p>
              )}
            </>
          )
        : showSpinner && <CircularProgress />}
    </>
  );
};

const defaultProps: OwnProps = {
  testConnection: false,
  testConnectionTimeout: 3000,
  showSpinner: false,
};
ImportProducts.defaultProps = defaultProps;

const mapStateToProps = (state: IState): StateProps => ({
  OAuthURL: state.shopify.OAuthURL,
  shopifyLastRun: state.shopify.lastRun,
  shopifyLastJob: state.shopify.lastJob,
  connectionStatus: state.shopify.connectionStatus,
  shopifyImportProductsStatus: state.shopify.importProductsStatus,
  shopifyShops: state.shopify.shops,
});

const mapDispatchToProps = {
  fetchOAuthUrlRequest: authActions.fetchOAuthUrlRequest,
  importProductsRequest: shopifyActions.importProductsRequest,
  testConnectionForShopRequest: shopifyActions.testConnectionForShopRequest,
};

export default connect<StateProps, DispatchProps, OwnProps, any>(
  mapStateToProps,
  mapDispatchToProps,
)(withLocalize(ImportProducts));
